// Звуковий сигнал нової нотатки/відповіді у треді заявки БпЛА.
//
// Синтезуємо два коротких тони через WebAudio ("уh-oh" у стилі старих месенджерів), а не
// возимо аудіофайл: не потрібен бінарник у репозиторії, немає питань щодо ліцензії на семпл,
// і сигнал не залежить від мережі — черговий чує його навіть на просілому каналі.
//
// Autoplay policy: браузер тримає AudioContext у 'suspended', доки у вкладці не було жодної
// взаємодії. Контекст піднімаємо на НАЙПЕРШОМУ жесті (plugins/uav-note-sound.client.ts), а не
// в момент нотатки: один будь-який клік за сесію — і далі всі сигнали лунають самі.

const NOTE_TONES: { freq: number, start: number, duration: number }[] = [
  {freq: 880, start: 0, duration: 0.12},    // "uh"
  {freq: 622, start: 0.13, duration: 0.20}, // "oh" — нижче й трохи довше
]

const PEAK_GAIN = 0.18 // помітно в тихому приміщенні, але не б'є по вухах у навушниках

// За замовчуванням звук ВИМКНЕНО: диспетчерську тримають відкритою й на спільних екранах,
// тож сигнал вмикає той, кому він потрібен. Вибір чергового переживає перезавантаження
// (localStorage, не Pinia). Ref — щоб перемикач у диспетчерській бачив зміну одразу.
const ENABLED_STORAGE_KEY = 'uavNoteSoundEnabled'
const muted = ref(true)
let mutedLoaded = false

// Сигнал, який браузер не пустив через autoplay policy — програємо його на першому ж жесті.
let pendingBlockedSound = false

let audioContext: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (audioContext) return audioContext
  const Ctor = window.AudioContext || (window as unknown as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext
  if (!Ctor) return null
  audioContext = new Ctor()
  return audioContext
}

// Ніде НЕ чекаємо на resume(): у Chrome без користувацького жесту цей проміс не відхиляється,
// а просто ніколи не резолвиться — await на ньому підвішує весь ланцюжок (і сигнал, і підказку).
function resumeInBackground(ctx: AudioContext): void {
  if (ctx.state === 'running') return
  ctx.resume().catch(() => {})
}

// Читаємо localStorage ліниво (SPA, але композабл смикається і з плагіна, і зі стора).
function loadMuted(): void {
  if (mutedLoaded || typeof window === 'undefined') return
  mutedLoaded = true
  muted.value = window.localStorage.getItem(ENABLED_STORAGE_KEY) !== '1'
}

export function useUavNoteSound() {
  loadMuted()

  function setMuted(value: boolean): void {
    muted.value = value
    if (typeof window === 'undefined') return
    if (value) window.localStorage.removeItem(ENABLED_STORAGE_KEY)
    else window.localStorage.setItem(ENABLED_STORAGE_KEY, '1')
  }

  // Викликається з першого ж жесту у вкладці. Порожній буфер — щоб на iOS Safari контекст
  // перейшов у 'running' саме зараз: там одного resume() без відтворення буває недостатньо.
  function unlockNoteSound(): void {
    const ctx = getAudioContext()
    if (!ctx) return
    try {
      resumeInBackground(ctx)
      const source = ctx.createBufferSource()
      source.buffer = ctx.createBuffer(1, 1, ctx.sampleRate)
      source.connect(ctx.destination)
      source.start(0)

      // Нотатка прилетіла до першої взаємодії зі сторінкою — сигнал тоді програти не можна було.
      // Догукуємо його зараз, на першому ж жесті: черговий не пропустить доповідь, і жодного
      // "дозвольте звук" питати не треба.
      if (pendingBlockedSound) {
        pendingBlockedSound = false
        setTimeout(() => playNoteSound(), 150) // контекст стає 'running' не миттєво
      }
    } catch {
      // Жест виявився "неповноцінним" (напр. programmatic click) — наступний спробує знову.
    }
  }

  function playNoteSound(): boolean {
    if (muted.value) return false
    const ctx = getAudioContext()
    if (!ctx) return false

    // Ще не було жодного жесту у вкладці — запам'ятовуємо, що сигнал винні, і програємо
    // його з unlockNoteSound(), щойно користувач хоч десь клікне.
    if (ctx.state !== 'running') {
      pendingBlockedSound = true
      resumeInBackground(ctx)
      return false
    }

    try {
      const now = ctx.currentTime
      for (const tone of NOTE_TONES) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'triangle' // м'якше за square, чутніше за sine
        osc.frequency.setValueAtTime(tone.freq, now + tone.start)

        // Коротка атака + експоненційний спад: без них на старті/кінці тону чути клац.
        gain.gain.setValueAtTime(0.0001, now + tone.start)
        gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, now + tone.start + 0.01)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.start + tone.duration)

        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(now + tone.start)
        osc.stop(now + tone.start + tone.duration + 0.02)
      }
      return true
    } catch {
      return false
    }
  }

  return {playNoteSound, unlockNoteSound, setMuted, muted: readonly(muted)}
}
